import React, { useState } from "react";
import { Link } from "react-router-dom";
import PayPalCheckout from "../components/PayPalCheckout";
import { HandHeart, ShieldStar, CheckCircle, Info, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

const PRESETS = [5, 10, 25, 50, 100];

export default function Donate() {
  const [amount, setAmount] = useState(25);
  const [custom, setCustom] = useState("");
  const [paid, setPaid] = useState(false);

  const effective = custom ? parseFloat(custom) : amount;
  const valid = effective >= 1 && effective <= 1000 && !Number.isNaN(effective);

  if (paid) {
    return (
      <main className="max-w-3xl mx-auto px-6 md:px-10 py-20 text-center" data-testid="donate-success">
        <div className="inline-flex h-20 w-20 rounded-full bg-emerald-100 items-center justify-center">
          <CheckCircle size={42} weight="fill" className="text-emerald-600" />
        </div>
        <h1 className="font-display text-3xl md:text-5xl font-black tracking-tight text-slate-900 mt-6">
          Thank you.
        </h1>
        <p className="text-slate-700 mt-4 leading-relaxed max-w-xl mx-auto">
          Your donation to Boot To Benefit helps keep this site free and supports veteran-focused organizations. We{`'`}ll publish a transparency update on how funds are deployed.
        </p>
        <div className="mt-8 flex gap-3 justify-center">
          <Link to="/" className="inline-flex items-center gap-2 bg-[#0033A0] hover:bg-[#002277] text-white font-semibold py-3 px-6 rounded-md">
            Back to home <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="max-w-5xl mx-auto px-6 md:px-10 py-14 md:py-20" data-testid="donate-page">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <span className="inline-flex items-center gap-2 bg-[#D90429]/10 text-[#D90429] px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
            <HandHeart size={14} weight="fill" /> Support our mission
          </span>
          <h1 className="font-display text-4xl md:text-6xl font-black tracking-tighter text-slate-900 mt-4 leading-[1.05]">
            Donate to Boot To Benefit.
          </h1>
          <p className="text-lg text-slate-700 mt-5 max-w-2xl leading-relaxed">
            We keep this site free, ad-free, and recruit-first. Your donation helps us pay for hosting, content updates, and the AI that powers Boot Buddy. We also donate a portion of all proceeds to veteran-support organizations.
          </p>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Box icon={ShieldStar} title="No ads, no spam" body="We don't sell your data or run ads. Donations and the optional $1 report fund the site." />
            <Box icon={HandHeart} title="Supports veterans" body="A meaningful share of net proceeds goes to vetted veteran-support nonprofits." />
            <Box icon={Info} title="Transparent" body="We post periodic transparency updates on what was raised, spent, and donated." />
          </div>

          <div className="mt-10 bg-slate-50 border border-slate-200 rounded-xl p-5 text-sm text-slate-700 leading-relaxed">
            <p className="font-semibold text-slate-900">A note on tax-deductibility</p>
            <p className="mt-1">
              Boot To Benefit is not a registered 501(c)(3). Your donation supports our operations and the veteran organizations we partner with, but it is <strong>not tax-deductible</strong> for U.S. donors. If you{`'`}d prefer to donate directly to a veteran nonprofit, we{`'`}ll happily point you toward several reputable options.
            </p>
          </div>
        </div>

        <aside className="lg:col-span-5">
          <div className="bg-white border border-slate-200 rounded-2xl p-6 lg:sticky lg:top-24" data-testid="donate-form">
            <h3 className="font-display font-bold text-xl text-slate-900">Pick an amount</h3>
            <p className="text-sm text-slate-500 mt-1">One-time donation in USD.</p>

            <div className="mt-5 grid grid-cols-5 gap-2">
              {PRESETS.map((v) => (
                <button
                  key={v}
                  onClick={() => { setAmount(v); setCustom(""); }}
                  data-testid={`donate-preset-${v}`}
                  className={`py-3 rounded-md border-2 font-display font-bold text-sm transition-all ${
                    !custom && amount === v
                      ? "border-[#0033A0] bg-[#0033A0] text-white"
                      : "border-slate-200 bg-white text-slate-800 hover:border-slate-400"
                  }`}
                >
                  ${v}
                </button>
              ))}
            </div>

            <div className="mt-4">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Or custom amount</label>
              <div className="mt-2 flex items-center bg-white border border-slate-300 rounded-md px-3 focus-within:ring-2 focus-within:ring-[#0033A0]">
                <span className="text-slate-500 text-sm">$</span>
                <input
                  type="number"
                  min="1"
                  max="1000"
                  step="1"
                  value={custom}
                  onChange={(e) => setCustom(e.target.value)}
                  placeholder="Enter amount"
                  data-testid="donate-custom-input"
                  className="flex-1 bg-transparent px-2 py-2.5 text-sm outline-none"
                />
                <span className="text-slate-400 text-xs">USD</span>
              </div>
              <p className="text-[11px] text-slate-500 mt-1">$1 minimum, $1,000 maximum.</p>
            </div>

            <div className="border-t border-slate-200 my-5" />

            <div className="flex justify-between items-baseline mb-4">
              <span className="text-slate-600 text-sm">You{`'`}ll donate</span>
              <span className="font-display font-bold text-2xl text-slate-900" data-testid="donate-total">
                ${valid ? effective.toFixed(2) : "—"}
              </span>
            </div>

            <PayPalCheckout
              purpose="donation"
              disabled={!valid}
              getPayload={async () => ({ amount: effective })}
              onPaid={(res) => {
                if (res?.paid) {
                  setPaid(true);
                  toast.success("Thank you for your donation!");
                } else {
                  toast.error("Payment didn't complete");
                }
              }}
              onError={(err) => {
                if (err?.canceled) return;
                toast.error("PayPal error — please try again");
              }}
            />

            <p className="text-[11px] text-slate-500 text-center mt-3 leading-relaxed">
              Securely processed by PayPal. We do not see or store your card details.
            </p>
          </div>
        </aside>
      </div>
    </main>
  );
}

function Box({ icon: Icon, title, body }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <Icon size={22} weight="duotone" className="text-[#0033A0]" />
      <h4 className="font-display font-bold text-slate-900 mt-2 text-sm">{title}</h4>
      <p className="text-xs text-slate-600 mt-1 leading-relaxed">{body}</p>
    </div>
  );
}
